import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff, KanbanSquare, Loader2, Megaphone, Zap, type LucideIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/entrar")({
  // A chave precisa ser opcional de verdade (ausente), e não presente com
  // valor undefined — senão o roteador exige `search` em toda navegação para cá.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search["redirect"] === "string" ? { redirect: search["redirect"] } : {},
  component: EntrarPage,
});

type Mode = "login" | "register";

/**
 * O que a clínica ganha, em três linhas.
 *
 * O login é a única tela em que o produto se apresenta — quem entra todo dia
 * passa por aqui, quem foi convidado vê isto antes de qualquer painel. Cada
 * item é uma promessa que o CRM cumpre de fato: origem de cada conversa,
 * fila de quem ainda não foi respondido e pipeline ligado à conversa.
 */
const PROMESSAS: { icon: LucideIcon; titulo: string; texto: string }[] = [
  {
    icon: Megaphone,
    titulo: "Do anúncio à venda",
    texto:
      "Cada conversa chega com a origem gravada, e a venda aparece ao lado do anúncio que a trouxe.",
  },
  {
    icon: Zap,
    titulo: "Resposta rápida",
    texto:
      "Instagram, Messenger e WhatsApp numa fila só, com quem ainda não foi respondido em destaque.",
  },
  {
    icon: KanbanSquare,
    titulo: "Pipeline que anda sozinho",
    texto:
      "Etapas, tarefas e lembretes seguem a conversa; o quadro mostra onde cada paciente parou.",
  },
];

function EntrarPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();

  const [modoEscolhido, setMode] = useState<Mode>("login");

  // O cadastro público é decisão do servidor (`PUBLIC_SIGNUP_ENABLED`). Sem a
  // resposta — ou com ele desligado, que é o padrão — a tela é só de login.
  const config = useQuery({
    queryKey: ["auth", "config"],
    queryFn: () => api.get<{ signupEnabled: boolean }>("/auth/config"),
    staleTime: 5 * 60_000,
  });
  const cadastroAberto = config.data?.signupEnabled === true;
  const mode: Mode = cadastroAberto ? modoEscolhido : "login";
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) void navigate({ to: redirect ?? "/painel" });
  }, [user, navigate, redirect]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setFieldErrors({});

    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "");
    const password = String(form.get("password") ?? "");

    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register({
          email,
          password,
          fullName: String(form.get("fullName") ?? ""),
          companyName: String(form.get("companyName") ?? "") || undefined,
        });
      }
      await navigate({ to: redirect ?? "/painel" });
    } catch (error) {
      if (error instanceof ApiError) {
        // Erros de validação vão para o campo certo; o resto vira toast.
        const byField = error.fieldErrors;
        if (Object.keys(byField).length > 0) setFieldErrors(byField);
        else toast.error(error.message);
      } else {
        toast.error("Não foi possível conectar ao servidor");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // Tela dividida em tela grande; no celular vira coluna (faixa de marca em
    // cima, formulário embaixo). `min-h-dvh` é permitido aqui: a rota é
    // pública e não vive dentro do AppShell.
    <div className="flex min-h-dvh flex-col bg-background lg:grid lg:grid-cols-2">
      <BrandPanel />

      <main className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-6 lg:py-12">
        <div className="w-full max-w-sm animate-rise">
          <Card className="shadow-panel">
            <CardContent className="p-6 sm:p-8">
              <div className="mb-6">
                <h1 className="font-display text-xl font-semibold leading-tight tracking-tight sm:text-2xl">
                  {mode === "login" ? "Entrar" : "Criar conta"}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {mode === "login"
                    ? "Use o e-mail e a senha da sua conta."
                    : "Crie a conta da sua clínica e convide a equipe depois."}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === "register" && (
                  <>
                    <Field
                      id="fullName"
                      label="Seu nome"
                      autoComplete="name"
                      required
                      error={fieldErrors["fullName"]}
                    />
                    <Field id="companyName" label="Nome da clínica" placeholder="Opcional" />
                  </>
                )}

                <Field
                  id="email"
                  label="E-mail"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  error={fieldErrors["email"]}
                />
                <PasswordField
                  id="password"
                  label="Senha"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  required
                  error={fieldErrors["password"]}
                  hint={mode === "register" ? "Mínimo de 12 caracteres" : undefined}
                />

                <Button type="submit" size="lg" className="mt-2 w-full" disabled={submitting}>
                  {submitting && <Loader2 className="animate-spin" />}
                  {submitting ? "Aguarde…" : mode === "login" ? "Entrar" : "Criar conta"}
                </Button>
              </form>

              {cadastroAberto ? (
                <p className="mt-5 text-center text-sm text-muted-foreground">
                  {mode === "login" ? "Não tem conta? " : "Já tem conta? "}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-sm"
                    onClick={() => {
                      setMode(mode === "login" ? "register" : "login");
                      setFieldErrors({});
                    }}
                  >
                    {mode === "login" ? "Criar agora" : "Entrar"}
                  </Button>
                </p>
              ) : (
                <p className="mt-5 text-center text-sm text-muted-foreground">
                  O acesso é por convite. Se ainda não tem conta, peça o link ao administrador da
                  sua empresa.
                </p>
              )}
            </CardContent>
          </Card>

          {/* No celular o painel de marca é só uma faixa, então os links
              legais ficam aqui embaixo para continuarem alcançáveis. */}
          <LegalLinks
            className="mt-6 justify-center text-muted-foreground lg:hidden"
            linkClassName="hover:text-foreground"
          />
        </div>
      </main>
    </div>
  );
}

/**
 * Painel de marca — metade esquerda em tela grande, faixa curta no celular.
 *
 * Usa o fundo da barra lateral (escuro nos dois temas) para a tela pública
 * ter a mesma cara do app por dentro. O brilho é o gradiente da marca
 * desfocado: dá o ar de produto sem carregar imagem nenhuma.
 */
function BrandPanel() {
  return (
    <aside className="relative isolate overflow-hidden bg-sidebar text-sidebar-foreground lg:sticky lg:top-0 lg:h-dvh">
      {/* Brilhos decorativos, fora da árvore de acessibilidade. */}
      <div
        aria-hidden
        className="bg-gradient-brand pointer-events-none absolute -left-24 -top-32 -z-10 size-[28rem] rounded-full opacity-25 blur-3xl"
      />
      <div
        aria-hidden
        className="bg-gradient-brand pointer-events-none absolute -bottom-40 -right-24 -z-10 hidden size-96 rounded-full opacity-15 blur-3xl lg:block"
      />

      {/* Celular: só a marca. */}
      <div className="flex items-center gap-2.5 px-4 py-4 sm:px-6 lg:hidden">
        <BrandMark />
        <span className="font-display text-[15px] font-semibold text-sidebar-accent-foreground">
          Revenue Nexus
        </span>
      </div>

      {/* Tela grande: marca, promessa e rodapé, distribuídos na altura. */}
      <div className="hidden h-full flex-col justify-between gap-10 p-10 lg:flex xl:p-14">
        <div className="flex items-center gap-2.5">
          <BrandMark />
          <span className="font-display text-[15px] font-semibold text-sidebar-accent-foreground">
            Revenue Nexus
          </span>
        </div>

        <div className="max-w-md">
          <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-sidebar-accent-foreground xl:text-4xl">
            O CRM que sabe de onde veio cada paciente.
          </h2>
          <ul className="mt-10 space-y-6">
            {PROMESSAS.map(({ icon: Icon, titulo, texto }) => (
              <li key={titulo} className="flex gap-4">
                <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-primary ring-1 ring-sidebar-border">
                  <Icon className="size-4" />
                </span>
                <div>
                  <p className="font-display text-sm font-semibold text-sidebar-accent-foreground">
                    {titulo}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-sidebar-foreground/70">{texto}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <LegalLinks
          className="text-sidebar-foreground/55"
          linkClassName="hover:text-sidebar-accent-foreground"
        />
      </div>
    </aside>
  );
}

/** O quadrado "RN" — o mesmo do cabeçalho da barra lateral. */
function BrandMark() {
  return (
    <span
      aria-hidden
      className="bg-gradient-brand inline-flex size-8 shrink-0 items-center justify-center rounded-lg font-display text-[11px] font-bold tracking-tight text-white shadow-card"
    >
      RN
    </span>
  );
}

/**
 * Rodapé legal: ano, privacidade e termos.
 *
 * As cores vêm de quem usa: sobre o painel escuro o hover é o branco da
 * barra lateral; sobre a página é o texto normal.
 */
function LegalLinks({ className, linkClassName }: { className?: string; linkClassName: string }) {
  const link = cn("transition-colors", linkClassName);
  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1 text-xs", className)}>
      <span>© {new Date().getFullYear()} Revenue Nexus</span>
      <Link to="/privacidade" className={link}>
        Privacidade
      </Link>
      <Link to="/termos" className={link}>
        Termos
      </Link>
    </div>
  );
}

type FieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  /** Algo por cima do campo, à direita (ex.: botão de mostrar senha). */
  trailing?: ReactNode;
};

function Field({ id, label, error, hint, trailing, className, ...props }: FieldProps) {
  // O leitor de tela anuncia o erro (ou a dica) junto com o campo.
  const describedBy = error ? `${id}-erro` : hint ? `${id}-dica` : undefined;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          name={id}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          className={className}
          {...props}
        />
        {trailing}
      </div>
      {error ? (
        <p id={`${id}-erro`} className="text-xs text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-dica`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Campo de senha com mostrar/ocultar.
 *
 * Senha de 12 caracteres digitada no celular erra com frequência; poder
 * conferir o que foi escrito evita a segunda tentativa. O estado é local:
 * ao trocar de modo o campo volta escondido.
 */
function PasswordField(props: Omit<FieldProps, "type" | "trailing">) {
  const [visivel, setVisivel] = useState(false);
  return (
    <Field
      {...props}
      type={visivel ? "text" : "password"}
      className="pr-10"
      trailing={
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="absolute right-0.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:bg-transparent hover:text-foreground"
          aria-label={visivel ? "Ocultar senha" : "Mostrar senha"}
          aria-pressed={visivel}
          onClick={() => setVisivel((v) => !v)}
        >
          {visivel ? <EyeOff /> : <Eye />}
        </Button>
      }
    />
  );
}
